import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getCommuteList,
  addCommuteToBusiness,
  deleteCommute,
  updateCommute,
} from "../../../../../../apis/Business/CommuteApi";
import { getTravelCommuteData } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const CommuteTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownMeans, setDropdownMeans] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getCommuteList({
      businessId,
      setObjects: setData,
    });
    getTravelCommuteData({ setDropdown: setDropdownMeans });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateCommute({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        means: editedItem["means.value"]
          ? editedItem["means.value"].value
          : editedItem["means"]
          ? editedItem["means"].value
          : null,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addCommuteToBusiness({
        businessId,
        ...newItem,
        means: newItem["means.value"] ? newItem["means.value"].value : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteCommute({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "employee_name",
        header: t("Employee name"),
        type: "text",
        disableSortBy: true,
        placeholder: t("NAME"),
        width: "120px",
      },
      {
        accessor: "company_vehicle",
        header: t("Company vehicle"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
      },
      {
        accessor: "means.value",
        header: t("MEANS"),
        type: "dropdown",
        dropdownData: dropdownMeans,
        disableSortBy: true,
      },

      {
        accessor: "transport_cost",
        header: `${t("DISTANCE_WORK")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: "km",
        width: "90px",
      },

      {
        accessor: "days_per_year",
        header: `${t("Days per year")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: t("EMPLOYEE_AMOUNT"),
        width: "90px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownMeans, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "means.value": dropdownMeans,
    }),
    [dropdownMeans]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default CommuteTable;
